/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  EventRuleSyntax,
  EventRuleSyntaxBeginsWith,
  EventRuleSyntaxExistence,
  EventRuleSyntaxNotEquals,
  EventRuleSyntaxNumeric,
} from '../../../../Services/event-bus';
import {
  AttributeFilter,
  AttributeRule,
} from '../../forms/EventSubscriptionBuilderForm/EventSubscriptionBuilderForm.defs';

/**
 * Converts the event syntax into an attribute filter for the UI.
 * @param {string} name The attribute name.
 * @param {EventRuleSyntax} eventRule The event matching rule.
 * @export
 * @returns {string} The event pattern syntax which represents the attribute filter.
 */
export const convertToAttributeFilter = (
  name: string,
  eventRule: EventRuleSyntax,
): Omit<AttributeFilter, 'id'> | null => {
  const attributeFilter = {
    name,
    value: '',
  } as AttributeFilter;

  // All event rules are wrapped inside an array
  const innerEventRule = eventRule[0];

  if (innerEventRule === null) {
    return {
      ...attributeFilter,
      rule: AttributeRule.IsNull,
    };
  }

  if (innerEventRule === '') {
    return {
      ...attributeFilter,
      rule: AttributeRule.IsEmpty,
    };
  }

  if (typeof innerEventRule === 'string') {
    return {
      ...attributeFilter,
      rule: AttributeRule.Equals,
      value: innerEventRule,
    };
  }

  if (typeof innerEventRule === 'object') {
    if (Object.hasOwn(innerEventRule, 'anything-but')) {
      const innerEventRule = (eventRule as EventRuleSyntaxNotEquals)[0];
      return {
        ...attributeFilter,
        rule: AttributeRule.NotEquals,
        value: innerEventRule['anything-but'][0],
      };
    }

    if (Object.hasOwn(innerEventRule, 'exists')) {
      const innerEventRule = (eventRule as EventRuleSyntaxExistence)[0];
      return {
        ...attributeFilter,
        rule: innerEventRule.exists ? AttributeRule.Exists : AttributeRule.NotExists,
      };
    }

    if (Object.hasOwn(innerEventRule, 'prefix')) {
      const innerEventRule = (eventRule as EventRuleSyntaxBeginsWith)[0];
      return {
        ...attributeFilter,
        rule: AttributeRule.BeginsWith,
        value: innerEventRule.prefix,
      };
    }

    if (Object.hasOwn(innerEventRule, 'numeric')) {
      const innerEventRule = (eventRule as EventRuleSyntaxNumeric)[0];
      attributeFilter.value = innerEventRule.numeric[1].toString();

      // eslint-disable-next-line default-case
      switch (innerEventRule.numeric[0]) {
        case AttributeRule.Equals:
          attributeFilter.rule = AttributeRule.Equals;
          break;
        case AttributeRule.LessThan:
          attributeFilter.rule = AttributeRule.LessThan;
          break;
        case AttributeRule.LessThanEquals:
          attributeFilter.rule = AttributeRule.LessThanEquals;
          break;
        case AttributeRule.GreaterThan:
          attributeFilter.rule = AttributeRule.GreaterThan;
          break;
        case AttributeRule.GreaterThanEquals:
          attributeFilter.rule = AttributeRule.GreaterThanEquals;
          break;
      }

      return attributeFilter;
    }
  }

  return null;
};
